<template>
  <Teleport to="body">
    <Transition name="toast-fade">
      <div v-if="visible" class="em-toast-overlay" @click.self="dismiss">
        <div class="em-toast" :class="`em-toast--${type}`">
          <div class="em-toast__icon">
            <IconRenderer :name="iconName" size="medium" />
          </div>
          <p class="em-toast__message">{{ message }}</p>
          <button class="em-toast__close" @click="dismiss">
            <IconRenderer name="close-outline" size="small" />
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import IconRenderer from '@/common/components/ui/icon/IconRenderer.vue';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Props {
  modelValue: boolean;
  message: string;
  type?: ToastType;
  duration?: number;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'success',
  duration: 5000,
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const visible = ref(props.modelValue);
let timer: ReturnType<typeof setTimeout> | null = null;

const iconName = computed(() => {
  const icons: Record<ToastType, string> = {
    success: 'checkmark-circle-outline',
    error: 'close-circle-outline',
    warning: 'warning-outline',
    info: 'information-circle-outline',
  };
  return icons[props.type];
});

const startTimer = () => {
  clearTimer();
  if (props.duration > 0) {
    timer = setTimeout(() => {
      dismiss();
    }, props.duration);
  }
};

const clearTimer = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
};

const dismiss = () => {
  visible.value = false;
  emit('update:modelValue', false);
  clearTimer();
};

watch(
  () => props.modelValue,
  (val) => {
    visible.value = val;
    if (val) {
      startTimer();
    } else {
      clearTimer();
    }
  }
);

onBeforeUnmount(() => {
  clearTimer();
});
</script>

<style scoped>
.em-toast-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: all;
}

.em-toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: var(--ion-radius-xl, 1rem);
  background: var(--ion-background-color, #fff);
  border: 1px solid var(--ion-input-border-color, #e0e0e0);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  min-width: 320px;
  max-width: 500px;
}

.em-toast--success {
  border-color: var(--ion-color-success, #2dd36f);
}

.em-toast--success .em-toast__icon {
  color: var(--ion-color-success, #2dd36f);
}

.em-toast--error {
  border-color: var(--ion-color-danger, #eb445a);
}

.em-toast--error .em-toast__icon {
  color: var(--ion-color-danger, #eb445a);
}

.em-toast--warning {
  border-color: var(--ion-color-warning, #ffc409);
}

.em-toast--warning .em-toast__icon {
  color: var(--ion-color-warning, #ffc409);
}

.em-toast--info {
  border-color: var(--ion-color-primary, #1c398e);
}

.em-toast--info .em-toast__icon {
  color: var(--ion-color-primary, #1c398e);
}

.em-toast__icon {
  flex-shrink: 0;
}

.em-toast__message {
  flex: 1;
  margin: 0;
  font-size: 0.9rem;
  color: var(--ion-text-color, #1e1e1e);
}

.em-toast__close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: var(--ion-text-color-secondary, #6c757d);
  transition: background 0.15s;
}

.em-toast__close:hover {
  background: var(--ion-background-color-quaternary, #e9ecef);
}

/* Transition */
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
